import * as gql from 'gql-query-builder';
import * as StringUtils from '../../utils/string-utils';
import {FieldKind, GraphQlField, GraphQlSchema, GraphQlType} from "../../types/GraphQlSchema";
import {Maybe} from "../../types/Maybe";
import {GraphQlQuery} from "../../types/GraphQlQuery";

const OBJECT_FIELDS = ["id", "__typename"];
const NAMED_OBJECT_FIELDS = ["id", "__typename", "name"];
const LITERAL_FIELDS = ["lang", "value"];

const DEFAULT_OBJECTS_LIMIT = 10;

const OBJECT_TYPE_QUERY_NAME = "ObjectTypeQuery";
const OBJECT_DETAILS_QUERY_NAME = "ObjectDetailsQuery";
const LOAD_MORE_OBJECTS_QUERY_NAME = "LoadMoreObjectsQuery"

type FieldSelection = string | Record<string, Array<FieldSelection>>;

/**
 * Creates a GraphQL query for retrieving object information.
 *
 * @param {string} objectId identifier of an object for which to retrieve the information
 * @returns {GraphQlQuery}
 */
export const getObjectTypeInfoQuery = (objectId: string): GraphQlQuery => {
  const query = gql.query(
    {
      operation: "object",
      variables: {
        ID: {value: objectId, type: '[ID!]!'}
      },
      fields: ["id", "__typename"]
    }, null, {
      operationName: OBJECT_TYPE_QUERY_NAME
    }
  );
  return {
    ...query,
    operationName: OBJECT_TYPE_QUERY_NAME
  };
};

/**
 * Creates a GraphQL query for retrieving object details. It creates a selection of all available
 * fields in the object's GraphQL type.
 *
 * @param {string} objectId the object for which to retrieve details
 * @param {GraphQlType} type the object's GraphQL type
 * @param {GraphQlSchema} graphQlSchema the GraphQL schema used to determine proper relational
 *                        fields selection
 * @returns {GraphQlQuery}
 */
export const getObjectDetailsQuery = (objectId: string, type: GraphQlType, graphQlSchema: GraphQlSchema): GraphQlQuery => {
  const query = gql.query(
    {
      operation: StringUtils.uncapitalize(type.name),
      variables: {
        ID: {value: objectId, type: '[ID!]!'}
      },
      fields: getFieldsSelection(type, graphQlSchema)
    },
    null,
    {
      operationName: OBJECT_DETAILS_QUERY_NAME
    }
  );
  return {
    ...query,
    operationName: OBJECT_DETAILS_QUERY_NAME
  };
};

/**
 * Creates a GraphQL query for loading additional object properties for specific object and field.
 *
 * @param {string} objectId identifier of an object for which to retrieve the information
 * @param {GraphQlType} type the object's GraphQL type
 * @param {GraphQlField} field the field for which to get more objects
 * @param {number} limit limits how objects to return
 * @param {number} offset defines how objects to skip
 * @returns {GraphQlQuery}
 */
export const getLoadMoreObjectsQuery = (objectId: string, type: GraphQlType, field: GraphQlField, limit: number, offset: number): GraphQlQuery => {
  const query = gql.query({
    operation: StringUtils.uncapitalize(type.name),
    variables: {
      ID: {value: objectId, type: '[ID!]!'}
    },
    fields: [
      getObjectFields(type.schemaRef, field, limit, offset)
    ]
  }, null, {
    operationName: LOAD_MORE_OBJECTS_QUERY_NAME
  });
  return {
    ...query,
    operationName: LOAD_MORE_OBJECTS_QUERY_NAME
  };
};

function getFieldsSelection(type: GraphQlType, graphQlSchema: GraphQlSchema): Array<FieldSelection> {
  if (!type.fields) {
    return [];
  }
  return type.fields.map(field => mapFieldSelection(graphQlSchema, field));
}

function mapFieldSelection(graphQlSchema: GraphQlSchema, field: GraphQlField) {
  if (field.kind === FieldKind.INTERFACE) {
    return getObjectFields(graphQlSchema, field, DEFAULT_OBJECTS_LIMIT, 0);
  }
  if (field.kind === FieldKind.OBJECT) {
    if (field.range === "Literal") {
      return getLiteralFields(field);
    }
    return getObjectFields(graphQlSchema, field, DEFAULT_OBJECTS_LIMIT, 0);
  }
  // Scalars
  return field.name;
}

function getObjectFields(graphQlSchema: GraphQlSchema, field: GraphQlField, limit: number, offset: number) {
  let fieldSelectionKey = field.name;
  if (hasPagingArguments(field)) {
    if (field.isMulti && offset > 0) {
      fieldSelectionKey = `${field.name} (limit: ${limit}, offset: ${offset})`;
    } else if(field.isMulti) {
      // NOTE: soaas 3.4 disallows 0 as offset...
      fieldSelectionKey = `${field.name} (limit: ${limit})`;
    }
  }
  return {
    [fieldSelectionKey]: hasNameField(graphQlSchema, field) ? NAMED_OBJECT_FIELDS : OBJECT_FIELDS
  };
}

function getLiteralFields(field: GraphQlField) {
  if (hasLangArgument(field)) {
    // Returns all lang strings, not only the default one
    return {
      [`${field.name}(lang: "ALL")`]: LITERAL_FIELDS
    };
  }
  return {
    [`${field.name}`]: LITERAL_FIELDS
  };
}

function hasNameField(graphQlSchema: GraphQlSchema, field: GraphQlField): boolean {
  if (!field.range) {
    return false
  }
  const rangeType: Maybe<GraphQlType> = graphQlSchema.objectTypes.get(field.range);
  if (!rangeType) {
    return false;
  }
  return !!rangeType.fields && rangeType.fields.some((field: GraphQlField) => field.name === "name");
}

function hasPagingArguments(field: GraphQlField) {
  return hasFieldArgument(field, "limit") && hasFieldArgument(field, "offset");
}

function hasLangArgument(field: GraphQlField) {
  return hasFieldArgument(field, "lang");
}

function hasFieldArgument(field: GraphQlField, argument: string) {
  return field.arguments.indexOf(argument) > -1;
}
